import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Play, X, CheckCircle, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Footer } from '../components/Footer';
import { VideoPlayer } from '../components/VideoPlayer';
import { useAnalytics } from '../hooks/useAnalytics';
const propertyData = {
  doge: {
    name: 'Own the Doge',
    tagline: "The Internet's Most Beloved Meme",
    description: `# About Own the Doge
Own the Doge is the exclusive rightsholder for Kabosu, the iconic Shiba Inu meme that captured the hearts of millions worldwide. From its humble beginnings as an internet joke to becoming a cultural phenomenon, Own the Doge represents authenticity, humor, and community.
## Why License Own the Doge?
- **Universal Recognition**: Known across all demographics and cultures
- **Viral Appeal**: Natural shareability and meme potential
- **Crypto Culture Icon**: Strong association with digital innovation
- **Timeless Humor**: "Much wow, very amaze" never gets old
## Product Categories
Perfect for apparel, accessories, tech products, and lifestyle goods that celebrate internet culture.`,
    licenseScope: {
      included: ['Original Kabosu image', 'Derivative works based on the original image', 'Associated meme phrases and formats'],
      excluded: ['Dogecoin cryptocurrency (unaffiliated)']
    },
    heroImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9GcybqBX3XwepUjNjqSxwK/Frame_3.jpg',
    galleryImages: ['https://uploadthingy.s3.us-west-1.amazonaws.com/9GcybqBX3XwepUjNjqSxwK/Frame_3.jpg'],
    stats: [{
      label: 'Social Reach',
      value: '20M+'
    }, {
      label: 'Meme Age',
      value: '10+ Years'
    }, {
      label: 'Global Recognition',
      value: '95%'
    }, {
      label: 'Market Cap Peak',
      value: '$88B'
    }],
    guidelines: `## Brand Guidelines
### Visual Identity
- Maintain the authentic Shiba Inu aesthetic
- Use Comic Sans MS or similar playful fonts
- Embrace the "broken English" meme format
### Color Palette
- Primary: #F7931A (Orange/Gold)
- Secondary: #FFFFFF (White)
- Accent: #000000 (Black)
### Tone & Voice
- Playful and self-aware
- Use iconic phrases: "Much wow", "Very [adjective]", "So [noun]"
- Keep it lighthearted and accessible
### Usage
- Celebrate internet culture and humor
- Maintain the DIY, grassroots aesthetic
- Avoid corporate or overly polished presentations`
  },
  luchadores: {
    name: 'Masked Republic® Luchadores',
    tagline: 'Legendary Mexican Wrestling Icons',
    description: `# About Masked Republic® Luchadores
Masked Republic is the world's leading lucha libre brand and talent representation company, representing 50+ iconic Mexican wrestling legends. This initial license grants access to 3 legendary luchadores, each with their own distinctive mask design, character, and fanbase.
## Why License Masked Republic® Luchadores?
- **Authentic Heritage**: Official representation of legendary Mexican wrestlers
- **Visual Impact**: Bold, colorful mask designs with instant recognition
- **Cross-Generational Appeal**: Beloved by families and wrestling fans worldwide with a far-reaching pop-culture kitsch appeal
- **Growing Market**: Expanding interest in authentic lucha libre culture. WWE's recent purchase of Lucha Libre AAA means millions of TV viewers globally are exposed to lucha libre and luchadores weekly on Netflix along with broadcast, cable & satellite throughout the world.
## Product Categories
Ideal for athletic wear, lifestyle apparel, accessories, home decor, collectibles, and products celebrating Mexican wrestling culture.`,
    featuredLuchadores: [{
      name: 'Solar',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/svofvPChbum4C7U7F9vvZR/Fullcolor.jpg',
      description: 'Iconic luchador, global star of the 70s-90s, still active today! One of the first luchadores to break through in Japan and introduce masked, trios tag team wrestling to the country. Star of CMLL, Lucha Libre AAA, UWA. More than 10 championship reigns.',
      era: '70s-90s Legend',
      achievements: '10+ Championship Reigns'
    }, {
      name: 'Vampiro',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8nGzFBgU718Q8uCVcwUUiJ/Fullcolor.jpg',
      description: "Legendary star who became a global superstar in WCW, TNA, NWA, and MTV's Wrestling Society X. Headlined Lucha Libre AAA's 2024 Mexico tour with sellout crowds. More than 1 million social media followers with two award-winning documentaries streaming on Tubi.",
      era: 'Global Superstar',
      achievements: '1M+ Social Followers'
    }, {
      name: 'Tigre W',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/csRchHk1SnL3xuXdhKe4rY/TigreMaskColorBackLogo.jpg',
      description: 'Rising star featured on the MaskedMania 2026 U.S. tour from Masked Republic and UTA. Anime inspired character driving crossover appeal with unique merchandise and moveset (with anime inspired attacks).',
      era: 'Rising Star',
      achievements: '2026 US Tour'
    }],
    licenseScope: {
      included: ['All 3 featured luchador likenesses and signature mask designs', 'Character names and associated branding for Solar, Vampiro, and Tigre W', 'Approved artwork and assets for all 3 luchadores', 'Logo and no-logo versions of all artwork for remixing'],
      excluded: ['Combining multiple luchadores in a single product design', 'Other Masked Republic roster members not included in this license', 'Lucha Libre AAA organizational marks']
    },
    heroImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/svofvPChbum4C7U7F9vvZR/Fullcolor.jpg',
    galleryImages: ['https://uploadthingy.s3.us-west-1.amazonaws.com/svofvPChbum4C7U7F9vvZR/Fullcolor.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/19TkHwEmUA4DgBhDSoqR32/LineartNotext.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/8nGzFBgU718Q8uCVcwUUiJ/Fullcolor.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/mG6h1QmieAzSKRhEE9Liba/FullLineartNoText.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/e3QGy63C74E8NkPpGewZUV/FullDesignColor.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/tP6XJFEUoNTq7hHd9tV9qr/CharWhite.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/36a4fddtsBP8esx1TjtmmG/CharaTitleColor.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/waiiULKjodfvLakPiTngQE/CharacterWhite.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/csRchHk1SnL3xuXdhKe4rY/TigreMaskColorBackLogo.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/xiuUADAQ32x4HsFVZN4WmJ/TigreMaskWhiteBack.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/uqHN7DfjCRt6cQmk7neeNi/CharacterBackLogo.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/pBC2nSgDVcvA1r2wuGSzS6/CharacterWhite.jpg'],
    stats: [{
      label: 'Total Roster',
      value: '50+ Luchadores'
    }, {
      label: 'This License',
      value: '3 Legends'
    }, {
      label: 'Heritage',
      value: '100+ Years'
    }, {
      label: 'Recent News',
      value: '2026 US Tour featuring Vampiro & Tigre W'
    }],
    guidelines: `## Brand Guidelines
### Licensing Structure
This license grants access to 3 legendary luchadores from Masked Republic's 50+ wrestler roster. Each product design must feature only one wrestler at a time. You may not combine multiple luchadores in a single product.
### Exclusive Access
The designs available through this program are exclusive to Printify licensees for at least 6 months from launch. These designs will not be made available on other platforms during this exclusivity period.
### Visual Identity
- Feature authentic mask designs specific to your chosen luchador
- Use bold, vibrant color combinations true to the character
- Incorporate traditional Mexican patterns and motifs
- Respect the unique visual identity of each wrestler
- Focus each product design on a single luchador
- Logo and no-logo versions available for creative remixing
### Color Palette
Colors vary by wrestler, but commonly include:
- Primary: #FF0000 (Red), #FFD700 (Gold)
- Secondary: #00FF00 (Green), #0000FF (Blue)
- Accent: #FF1493 (Pink), #000000 (Black)
### Cultural Respect
- Honor the tradition and heritage of Lucha Libre
- Work with authentic, approved mask designs
- Avoid stereotypes or caricatures
- Celebrate the athleticism and artistry of each luchador
### Typography
- Bold, impactful fonts for headlines
- Spanish language options encouraged
- Maintain readability with dynamic layouts
### Usage Guidelines
- Each product may only feature one luchador
- Do not combine multiple wrestlers in a single design
- This license covers Solar, Vampiro, and Tigre W only
- Masked Republic approval required for all designs
- Follow standard approval process through Spaceport`
  },
  'national-lampoon': {
    name: 'National Lampoon',
    tagline: 'Legendary Comedy Since 1970',
    description: `# About National Lampoon
National Lampoon is an iconic American comedy brand that has defined humor for over five decades. From groundbreaking magazine satire to blockbuster films, National Lampoon represents irreverent, intelligent comedy.
## Why License National Lampoon?
- **Brand Legacy**: 50+ years of comedy excellence
- **Multi-Media Success**: Magazine, films, radio, digital content
- **Nostalgic Appeal**: Strong connection with Gen X and Millennials
- **Timeless Humor**: Classic comedy that remains relevant
## Product Categories
Perfect for apparel, accessories, collectibles, and lifestyle goods that celebrate comedy, nostalgia, and irreverent humor.`,
    licenseScope: {
      included: ['Iconic lines from scripts', 'Magazine covers and artwork', 'National Lampoon logo and branding'],
      excluded: ['Film footage or video content', 'Actor likenesses']
    },
    heroImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6YjQpLzsjA56uDssy6GJo4/download_%284%29.png',
    galleryImages: ['https://uploadthingy.s3.us-west-1.amazonaws.com/6nHDXapGKiTZny7wCooTm2/download_%286%29.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/kTAJ96Z4pVekTTmsmNCJrh/download_%285%29.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/hvGnkm9BdUMTqXh1GL7xR2/download_%283%29.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/98MKhSkeYtSEdiHQD5M7bg/download_%282%29.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/vUm8tH8dEQ4fjCKHb5VT85/download_%281%29.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/fvXSHkAUco7hbzEyueY69n/10.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/6n85Hcj3uzjtg7vcgPubFQ/Clarks-Tree-Farm-national-lampoon1.png'],
    stats: [{
      label: 'Years Active',
      value: '50+'
    }, {
      label: 'Total Revenue',
      value: 'Over $1Billion in revenues (inflation adjusted)'
    }, {
      label: 'Fan Base',
      value: '15M+'
    }, {
      label: 'Content Archive',
      value: '50-year archive of print, audio, and video content'
    }],
    guidelines: `## Brand Guidelines
### Visual Identity
- Use the iconic red "NL" logo prominently
- Incorporate retro 70s-80s design elements
- Reference classic film imagery when appropriate
### Color Palette
- Primary: #DC143C (Crimson Red)
- Secondary: #FFFFFF (White)
- Accent: #FFD700 (Gold), #000000 (Black)
### Tone & Voice
- Irreverent but intelligent humor
- Satirical and self-aware
- Nostalgic yet contemporary
- Avoid offensive or insensitive content
### Typography
- Bold, impactful headlines
- Retro fonts for vintage appeal
- Clean sans-serif for modern applications
### Content Guidelines
- Reference classic National Lampoon properties
- Maintain the spirit of intelligent satire
- Appeal to comedy enthusiasts and nostalgia seekers`
  }
};
export function PropertyDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const {
    trackEvent
  } = useAnalytics();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showHeaderCTA, setShowHeaderCTA] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState<string | null>(null);
  const property = propertyData[id as keyof typeof propertyData];
  useEffect(() => {
    window.scrollTo(0, 0);
    // Set page title
    if (property) {
      document.title = `${property.name} - Spaceport`;
    }
    // Track property page view
    if (id) {
      trackEvent('property_view', {
        property_id: id,
        property_name: property?.name
      });
    }
  }, [id, property, trackEvent]);
  useEffect(() => {
    const handleScroll = () => {
      // Show header CTA when scrolled past 70vh (hero section height)
      const scrollPosition = window.scrollY;
      const heroHeight = window.innerHeight * 0.7;
      setShowHeaderCTA(scrollPosition > heroHeight);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  if (!property) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center" data-id="element-210">
        <div className="text-center" data-id="element-211">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-212">
            Property Not Found
          </h1>
          <button onClick={() => {
          trackEvent('navigation_click', {
            destination: '/',
            source: 'return_home'
          });
          navigate('/');
        }} className="text-blue-600 hover:text-blue-700" data-id="element-213">
            Return to Home
          </button>
        </div>
      </div>;
  }
  const handleJoinEarlyAccess = () => {
    trackEvent('cta_click', {
      cta_type: 'join_early_access_property',
      destination: 'https://portal.spaceport.xyz/login?opportunity=printify'
    });
    window.location.href = 'https://portal.spaceport.xyz/login?opportunity=printify';
  };
  const handleWatchTutorial = () => {
    trackEvent('video_open', {
      video_url: 'https://video.squarespace-cdn.com/content/v1/6356d3a6954945102e51363f/b6b40d9c-68b1-44cb-99c2-3ca936c099b9/playlist.m3u8'
    });
    setIsVideoModalOpen(true);
  };
  const handleCloseVideo = () => {
    trackEvent('video_close', {
      video_url: 'https://video.squarespace-cdn.com/content/v1/6356d3a6954945102e51363f/b6b40d9c-68b1-44cb-99c2-3ca936c099b9/playlist.m3u8'
    });
    setIsVideoModalOpen(false);
  };
  const handleGalleryImageClick = (image: string, index: number) => {
    trackEvent('gallery_image_click', {
      property_id: id,
      image_index: index
    });
    setSelectedGalleryImage(image);
  };
  const handleBackToCatalog = () => {
    trackEvent('navigation_click', {
      destination: '/',
      source: 'back_to_catalog'
    });
    navigate('/');
    setTimeout(() => {
      const catalogSection = document.querySelector('#ip-catalog');
      catalogSection?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  const nextImage = () => {
    setCurrentImageIndex(prevIndex => (prevIndex + 1) % property.galleryImages.length);
  };
  const prevImage = () => {
    setCurrentImageIndex(prevIndex => (prevIndex - 1 + property.galleryImages.length) % property.galleryImages.length);
  };
  return <div className="w-full min-h-screen bg-white" data-id="element-214">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-sm border-b border-white/10 fixed top-0 left-0 right-0 z-50" data-id="element-215">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between" data-id="element-216">
          <div className="flex items-center gap-4" data-id="element-217">
            <button onClick={() => {
            trackEvent('navigation_click', {
              destination: '/',
              source: 'back_button'
            });
            navigate('/');
          }} className="text-white hover:text-gray-200 transition-colors" data-id="element-218">
              <ArrowLeft className="w-5 h-5" data-id="element-219" />
            </button>
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/64JBjBQwTxv1wFV6Qexq11/Group_1000011127.png" alt="Spaceport" className="h-8 md:h-10 w-auto object-contain cursor-pointer" onClick={() => {
            trackEvent('navigation_click', {
              destination: '/',
              source: 'logo_click'
            });
            navigate('/');
          }} data-id="element-220" />
          </div>
          <AnimatePresence data-id="element-221">
            {showHeaderCTA && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="flex items-center gap-3" data-id="element-222">
                <button onClick={handleWatchTutorial} className="flex items-center gap-2 px-6 py-2 bg-white text-black text-lg font-light rounded-full hover:shadow-lg transition-all duration-300" data-id="element-223">
                  <Play className="w-4 h-4" fill="currentColor" data-id="element-224" />
                  <span className="whitespace-nowrap" data-id="element-225">Watch Tutorial</span>
                </button>
                <button onClick={() => window.location.href = 'https://portal.spaceport.xyz/license/buy/bundle/eJwNyskRwDAIBLCGsjOA1wflcDj9l5DoLbXNfU3h4wg43kSe5aCEU1m62h7e3X1XYwyf4CQR1oVZFWGSOv7kO7skDZU3QTOBKxsaPNn6Rm35APmKHI8?distributionAuth=printify'} className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-light rounded-full hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300" data-id="element-226">
                  <span className="whitespace-nowrap" data-id="element-227">Get Your License</span>
                  <ArrowRight className="w-4 h-4" data-id="element-228" />
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </header>
      {/* Hero Section */}
      <section className="relative w-full overflow-hidden" style={{
      height: '70vh'
    }} data-id="element-229">
        <img src={property.heroImage} alt={property.name} className="w-full h-full object-cover object-top" data-id="element-230" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-231" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12" data-id="element-232">
          <div className="max-w-7xl mx-auto" data-id="element-233">
            {/* Breadcrumb */}
            <button onClick={handleBackToCatalog} className="flex items-center gap-2 text-white/90 hover:text-white transition-colors text-sm font-medium mb-6" data-id="element-234">
              <span data-id="element-235">IP CATALOG</span>
              <ChevronRight className="w-4 h-4" data-id="element-236" />
              <span className="text-white" data-id="element-237">{property.name}</span>
            </button>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-238">
              <div data-id="element-239">
                <motion.h1 initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} className="text-5xl md:text-6xl lg:text-8xl font-bold text-white mb-4" data-id="element-240">
                  {property.name}
                </motion.h1>
                <motion.p initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2
              }} className="text-xl md:text-2xl text-white/90" data-id="element-241">
                  {property.tagline}
                </motion.p>
              </div>
              <motion.button initial={{
              opacity: 0,
              x: 30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.4
            }} onClick={() => window.location.href = 'https://portal.spaceport.xyz/license/buy/bundle/eJwNyskRwDAIBLCGsjOA1wflcDj9l5DoLbXNfU3h4wg43kSe5aCEU1m62h7e3X1XYwyf4CQR1oVZFWGSOv7kO7skDZU3QTOBKxsaPNn6Rm35APmKHI8?distributionAuth=printify'} className="flex items-center justify-center gap-3 w-full md:w-auto px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-2xl font-light rounded-full hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 whitespace-nowrap" data-id="element-242">
                <span data-id="element-243">Get Your License</span>
                <ArrowRight className="w-6 h-6" data-id="element-244" />
              </motion.button>
            </div>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 pt-0 pb-16" data-id="element-245">
        {/* Stats Section */}
        <section className="mt-20 mb-20" data-id="element-246">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-247">
            {property.stats.map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200" data-id="element-248">
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-249">
                  {stat.value}
                </div>
                <div className="text-sm uppercase tracking-wider text-gray-600 font-medium" data-id="element-250">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </section>
        {/* Description Section */}
        <section className="mb-20" data-id="element-251">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-252">
            <div className="prose prose-lg max-w-none" data-id="element-253">
              <ReactMarkdown components={{
              h1: ({
                children
              }) => <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-255">
                      {children}
                    </h2>,
              h2: ({
                children
              }) => <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-256">
                      {children}
                    </h3>,
              p: ({
                children
              }) => <p className="text-lg text-gray-700 mb-4 leading-relaxed" data-id="element-257">
                      {children}
                    </p>,
              ul: ({
                children
              }) => <ul className="space-y-3 mb-6" data-id="element-258">{children}</ul>,
              li: ({
                children
              }) => <li className="flex items-start gap-3 text-gray-700" data-id="element-259">
                      <span className="text-blue-600 mt-1.5 flex-shrink-0" data-id="element-260">
                        •
                      </span>
                      <span className="text-lg leading-relaxed" data-id="element-261">
                        {children}
                      </span>
                    </li>,
              strong: ({
                children
              }) => <strong className="font-bold text-gray-900" data-id="element-262">
                      {children}
                    </strong>
            }} data-id="element-254">
                {property.description}
              </ReactMarkdown>
            </div>
            <div className="relative" data-id="element-263">
              <div className="sticky top-24" data-id="element-264">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200" data-id="element-265">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-266">
                    Quick Facts
                  </h3>
                  <div className="space-y-4" data-id="element-267">
                    {property.stats.map((stat, index) => <div key={index} className="flex items-baseline justify-between pb-4 border-b border-gray-200 last:border-0" data-id="element-268">
                        <span className="text-sm uppercase tracking-wider text-gray-600 font-medium" data-id="element-269">
                          {stat.label}
                        </span>
                        <span className={`text-right font-bold text-gray-900 ${stat.value.length > 15 ? 'text-base' : 'text-2xl'}`} data-id="element-270">
                          {stat.value}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Exclusive Designs Callout - Only for luchadores property */}
        {id === 'luchadores' && <section className="mb-20" data-id="element-271">
            <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-3xl p-1 overflow-hidden" data-id="element-272">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 opacity-50 blur-2xl animate-pulse" data-id="element-273" />
              <div className="relative bg-white rounded-3xl p-8 md:p-12" data-id="element-274">
                <div className="flex flex-col md:flex-row md:items-start gap-6 mb-6" data-id="element-275">
                  <div className="flex-shrink-0 mx-auto md:mx-0" data-id="element-276">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center" data-id="element-277">
                      <Sparkles className="w-8 h-8 text-white" data-id="element-278" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-279">
                    <div className="inline-block bg-gradient-to-r from-amber-500 to-red-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-280">
                      LIMITED TIME EXCLUSIVE
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-281">
                      EXCLUSIVE DESIGNS FOR PRINTIFY
                    </h2>
                    <p className="text-xl text-gray-700 mb-6 leading-relaxed" data-id="element-282">
                      The artwork and designs available through this program are{' '}
                      <span className="font-bold text-orange-600" data-id="element-283">
                        exclusive to Printify licensees for at least 6 months
                        from launch
                      </span>
                      . These designs will not be made available on other
                      platforms during this exclusivity period.
                    </p>
                    <div className="grid md:grid-cols-3 gap-4" data-id="element-284">
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-200" data-id="element-285">
                        <div className="text-3xl font-bold text-amber-600 mb-2" data-id="element-286">
                          6+
                        </div>
                        <div className="text-sm font-semibold text-gray-700" data-id="element-287">
                          Months Exclusive Access
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border-2 border-orange-200" data-id="element-288">
                        <div className="text-3xl font-bold text-orange-600 mb-2" data-id="element-289">
                          0
                        </div>
                        <div className="text-sm font-semibold text-gray-700" data-id="element-290">
                          Other Platforms
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border-2 border-red-200" data-id="element-291">
                        <div className="text-3xl font-bold text-red-600 mb-2" data-id="element-292">
                          100%
                        </div>
                        <div className="text-sm font-semibold text-gray-700" data-id="element-293">
                          Printify Exclusive
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>}
        {/* Featured Luchadores Section - Only for luchadores property */}
        {id === 'luchadores' && property.featuredLuchadores && <section className="mb-20" data-id="element-294">
            <div className="bg-gradient-to-br from-red-50 via-yellow-50 to-green-50 rounded-3xl p-8 md:p-12 border-2 border-red-200" data-id="element-295">
              <div className="text-center mb-12" data-id="element-296">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-297">
                  FEATURED LUCHADORES
                </h2>
                <p className="text-xl text-gray-700 max-w-3xl mx-auto" data-id="element-298">
                  This license includes 3 legendary wrestlers from Masked
                  Republic's roster of 50+ luchadores
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8" data-id="element-299">
                {property.featuredLuchadores.map((luchador, index) => <motion.div key={luchador.name} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.2
            }} className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 group p-6" data-id="element-300">
                    <div className="mb-4" data-id="element-301">
                      <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-302">
                        {luchador.era}
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-303">
                        {luchador.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-4" data-id="element-304">
                        <CheckCircle className="w-5 h-5 text-green-600" data-id="element-305" />
                        <span className="text-sm font-semibold text-green-700" data-id="element-306">
                          {luchador.achievements}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed" data-id="element-307">
                      {luchador.description}
                    </p>
                  </motion.div>)}
              </div>
            </div>
          </section>}
        {/* License Scope Section */}
        <section className="mb-20" data-id="element-308">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12 border border-blue-100" data-id="element-309">
            <h2 className="text-4xl font-bold text-gray-900 mb-8" data-id="element-310">
              LICENSE SCOPE
            </h2>
            <div className="grid md:grid-cols-2 gap-8" data-id="element-311">
              <div data-id="element-312">
                <h3 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2" data-id="element-313">
                  <CheckCircle className="w-6 h-6" data-id="element-314" />
                  What's Included
                </h3>
                <ul className="space-y-3" data-id="element-315">
                  {property.licenseScope.included.map((item, index) => <li key={index} className="flex items-start gap-3 text-gray-700" data-id="element-316">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-317" />
                      <span className="text-lg" data-id="element-318">{item}</span>
                    </li>)}
                </ul>
              </div>
              {property.licenseScope.excluded.length > 0 && <div data-id="element-319">
                  <h3 className="text-2xl font-bold text-red-700 mb-4 flex items-center gap-2" data-id="element-320">
                    <X className="w-6 h-6" data-id="element-321" />
                    Not Included
                  </h3>
                  <ul className="space-y-3" data-id="element-322">
                    {property.licenseScope.excluded.map((item, index) => <li key={index} className="flex items-start gap-3 text-gray-700" data-id="element-323">
                        <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-324" />
                        <span className="text-lg" data-id="element-325">{item}</span>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </section>
        {/* Image Gallery */}
        <section className="mb-20" data-id="element-326">
          <h2 className="text-4xl font-bold text-gray-900 mb-8" data-id="element-327">
            BRAND PREVIEW
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-328">
            Preview imagery showcasing what this IP covers. Full asset library
            available upon licensing.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-329">
            {property.galleryImages.map((image, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} onClick={() => {
            trackEvent('gallery_image_click', {
              property_id: id,
              image_index: index
            });
            setSelectedGalleryImage(image);
          }} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer group border border-gray-200" data-id="element-330">
                <img src={image} alt={`Gallery ${index + 1}`} className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" data-id="element-331" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" data-id="element-332" />
              </motion.div>)}
          </div>
        </section>
        {/* Acceptable Remixing Examples Section */}
        <section className="mb-20" data-id="element-333">
          <div className="mb-12" data-id="element-334">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-335">
              ACCEPTABLE REMIXING EXAMPLES
            </h2>
            <p className="text-xl text-gray-600" data-id="element-336">
              Learn what makes a great remix by understanding our guidelines.
              These examples show how to create original designs while
              respecting the IP.
            </p>
          </div>
          {/* Doge Example */}
          {id === 'doge' && <div className="grid md:grid-cols-2 gap-8" data-id="element-337">
              {/* Left side - Stacked images */}
              <div className="space-y-6" data-id="element-338">
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-2xl overflow-hidden shadow-xl" data-id="element-339">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/877UaEXmoCLQvPaXianjTw/Frame_46.jpg" alt="Christmas Doge Remix Example" className="w-full h-auto" data-id="element-340" />
                </motion.div>
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-white rounded-2xl overflow-hidden shadow-xl" data-id="element-341">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/e55xJ3iGmbyD4yQaDkyN3U/83fd5345-5ef2-4015-b1fc-7fec6338481e.jpg" alt="Christmas Doge T-Shirt Mockup" className="w-full h-auto" data-id="element-342" />
                </motion.div>
              </div>
              {/* Right side - Content */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="space-y-6" data-id="element-343">
                <div className="bg-white rounded-3xl overflow-hidden border-2 border-gray-200 hover:border-blue-300 transition-colors duration-300 shadow-xl" data-id="element-344">
                  <div className="p-8 md:p-12" data-id="element-345">
                    <div className="mb-8" data-id="element-346">
                      <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-347">
                        EXAMPLE SEASONAL REMIX
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-348">
                        Christmas Doge
                      </h3>
                      <p className="text-lg text-gray-600 mb-8" data-id="element-349">
                        This remix takes the iconic Doge image and adds seasonal
                        elements (Santa hat) while preserving the character's
                        recognizable features and playful expression.
                      </p>
                    </div>
                    {/* Why This Works */}
                    <div className="mb-8" data-id="element-350">
                      <h4 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2" data-id="element-351">
                        <CheckCircle className="w-6 h-6" data-id="element-352" />
                        Why This Works
                      </h4>
                      <ul className="space-y-3" data-id="element-353">
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-354">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-355" />
                          <span data-id="element-356">
                            Uses official Doge artwork as the foundation
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-357">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-358" />
                          <span data-id="element-359">
                            Adds creative seasonal elements (Santa hat)
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-360">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-361" />
                          <span data-id="element-362">
                            Maintains Doge's iconic expression and features
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-363">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-364" />
                          <span data-id="element-365">
                            Preserves the playful, meme-friendly aesthetic
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-366">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-367" />
                          <span data-id="element-368">
                            Creates timely, marketable holiday merchandise
                          </span>
                        </li>
                      </ul>
                    </div>
                    {/* Remix Designs that Would Not Work - Now inside Why This Works */}
                    <div className="bg-red-50 rounded-2xl border-2 border-red-200 p-6" data-id="element-369">
                      <h4 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2" data-id="element-370">
                        <X className="w-6 h-6" data-id="element-371" />
                        Remix Designs that Would Not Work
                      </h4>
                      <ul className="space-y-3" data-id="element-372">
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-373">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-374" />
                          <span data-id="element-375">
                            Using a different dog breed or unofficial image
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-376">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-377" />
                          <span data-id="element-378">
                            Heavily distorting Doge's recognizable features
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-379">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-380" />
                          <span data-id="element-381">
                            Combining with other licensed characters or IPs
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-382">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-383" />
                          <span data-id="element-384">
                            Using poor quality or pixelated source images
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-385">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-386" />
                          <span data-id="element-387">
                            Adding offensive or inappropriate elements
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>}
          {/* National Lampoon Example */}
          {id === 'national-lampoon' && <div className="grid md:grid-cols-2 gap-8" data-id="element-388">
              {/* Left side - Stacked images */}
              <div className="space-y-6" data-id="element-389">
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-2xl overflow-hidden shadow-xl" data-id="element-390">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/k2npXr77uynNKGat6ni29o/nl_remix.jpg" alt="National Lampoon Remix Example" className="w-full h-auto" data-id="element-391" />
                </motion.div>
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-white rounded-2xl overflow-hidden shadow-xl" data-id="element-392">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vtEJKtU5a9YNL9HpE3N3ro/nl_shirt.jpg" alt="National Lampoon T-Shirt Mockup" className="w-full h-auto" data-id="element-393" />
                </motion.div>
              </div>
              {/* Right side - Content */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="space-y-6" data-id="element-394">
                <div className="bg-white rounded-3xl overflow-hidden border-2 border-gray-200 hover:border-blue-300 transition-colors duration-300 shadow-xl" data-id="element-395">
                  <div className="p-8 md:p-12" data-id="element-396">
                    <div className="mb-8" data-id="element-397">
                      <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-398">
                        EXAMPLE SEASONAL REMIX
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-399">
                        National Lampoon Holiday
                      </h3>
                      <p className="text-lg text-gray-600 mb-8" data-id="element-400">
                        This remix takes the iconic National Lampoon logo and
                        adds festive holiday elements like Christmas lights and
                        Santa hats while maintaining the brand's signature
                        comedy style.
                      </p>
                    </div>
                    {/* Why This Works */}
                    <div className="mb-8" data-id="element-401">
                      <h4 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2" data-id="element-402">
                        <CheckCircle className="w-6 h-6" data-id="element-403" />
                        Why This Works
                      </h4>
                      <ul className="space-y-3" data-id="element-404">
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-405">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-406" />
                          <span data-id="element-407">
                            Uses official National Lampoon logo as foundation
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-408">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-409" />
                          <span data-id="element-410">
                            Adds seasonal holiday decorations creatively
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-411">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-412" />
                          <span data-id="element-413">
                            Maintains the iconic yellow and red brand colors
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-414">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-415" />
                          <span data-id="element-416">
                            Preserves the comedy brand's irreverent spirit
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-417">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-418" />
                          <span data-id="element-419">
                            Creates timely merchandise for holiday season
                          </span>
                        </li>
                      </ul>
                    </div>
                    {/* Remix Designs that Would Not Work - Now inside Why This Works */}
                    <div className="bg-red-50 rounded-2xl border-2 border-red-200 p-6" data-id="element-420">
                      <h4 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2" data-id="element-421">
                        <X className="w-6 h-6" data-id="element-422" />
                        Remix Designs that Would Not Work
                      </h4>
                      <ul className="space-y-3" data-id="element-423">
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-424">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-425" />
                          <span data-id="element-426">
                            Redesigning or altering the core logo typography
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-427">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-428" />
                          <span data-id="element-429">
                            Changing the brand colors to non-recognizable hues
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-430">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-431" />
                          <span data-id="element-432">
                            Combining with other comedy brands or properties
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-433">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-434" />
                          <span data-id="element-435">
                            Using unofficial or recreated logo versions
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-436">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-437" />
                          <span data-id="element-438">
                            Adding elements that contradict brand values
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>}
          {/* Luchadores Example */}
          {id === 'luchadores' && <div className="grid md:grid-cols-2 gap-8" data-id="element-439">
              {/* Left side - Stacked images */}
              <div className="space-y-6" data-id="element-440">
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-2xl overflow-hidden shadow-xl" data-id="element-441">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ur1WdFKSqq6Fkb5MH29wmi/louchadors_remix.jpg" alt="Luchadores Remix Example" className="w-full h-auto" data-id="element-442" />
                </motion.div>
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-white rounded-2xl overflow-hidden shadow-xl" data-id="element-443">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/p8EBLJ59McWYj6WJQ91C3X/louchadors_shirt.jpg" alt="Luchadores T-Shirt Mockup" className="w-full h-auto" data-id="element-444" />
                </motion.div>
              </div>
              {/* Right side - Content */}
              <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="space-y-6" data-id="element-445">
                <div className="bg-white rounded-3xl overflow-hidden border-2 border-gray-200 hover:border-blue-300 transition-colors duration-300 shadow-xl" data-id="element-446">
                  <div className="p-8 md:p-12" data-id="element-447">
                    <div className="mb-8" data-id="element-448">
                      <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-449">
                        EXAMPLE SEASONAL REMIX
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-450">
                        Holiday Luchador
                      </h3>
                      <p className="text-lg text-gray-600 mb-8" data-id="element-451">
                        This remix features a licensed luchador character with
                        festive holiday elements like Santa hats and Christmas
                        lights while preserving the wrestler's authentic mask
                        design and dynamic pose.
                      </p>
                    </div>
                    {/* Why This Works */}
                    <div className="mb-8" data-id="element-452">
                      <h4 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2" data-id="element-453">
                        <CheckCircle className="w-6 h-6" data-id="element-454" />
                        Why This Works
                      </h4>
                      <ul className="space-y-3" data-id="element-455">
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-456">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-457" />
                          <span data-id="element-458">
                            Uses official luchador artwork and mask design
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-459">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-460" />
                          <span data-id="element-461">
                            Adds creative seasonal elements tastefully
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-462">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-463" />
                          <span data-id="element-464">
                            Maintains the authentic mask colors and patterns
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-465">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-466" />
                          <span data-id="element-467">
                            Preserves the dynamic wrestling pose and energy
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-468">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-469" />
                          <span data-id="element-470">Features only one luchador per design</span>
                        </li>
                      </ul>
                    </div>
                    {/* Remix Designs that Would Not Work - Now inside Why This Works */}
                    <div className="bg-red-50 rounded-2xl border-2 border-red-200 p-6" data-id="element-471">
                      <h4 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2" data-id="element-472">
                        <X className="w-6 h-6" data-id="element-473" />
                        Remix Designs that Would Not Work
                      </h4>
                      <ul className="space-y-3" data-id="element-474">
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-475">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-476" />
                          <span data-id="element-477">
                            Combining multiple luchadores in one design
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-478">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-479" />
                          <span data-id="element-480">
                            Altering the authentic mask design or colors
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-481">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-482" />
                          <span data-id="element-483">Using non-licensed luchador characters</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-484">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-485" />
                          <span data-id="element-486">
                            Creating stereotypical or disrespectful imagery
                          </span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-700" data-id="element-487">
                          <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-488" />
                          <span data-id="element-489">Mixing with other licensed IPs or brands</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>}
          {/* Guidelines Callout */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border-2 border-blue-200" data-id="element-490">
            <div className="flex items-start gap-4" data-id="element-491">
              <div className="flex-shrink-0" data-id="element-492">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center" data-id="element-493">
                  <Sparkles className="w-6 h-6 text-white" data-id="element-494" />
                </div>
              </div>
              <div data-id="element-495">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-496">
                  Need Help With Your Design?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-497">
                  All designs are reviewed within 24 hours. Our team will
                  provide feedback and guidance to ensure your remix meets our
                  standards. When in doubt, submit your design and we'll help
                  you refine it!
                </p>
                <button onClick={handleJoinEarlyAccess} className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300" data-id="element-498">
                  <span data-id="element-499">Get Started</span>
                  <ArrowRight className="w-4 h-4" data-id="element-500" />
                </button>
              </div>
            </div>
          </motion.div>
        </section>
        {/* CTA Section */}
        <section className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-12 text-center" data-id="element-501">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-502">
            READY TO LICENSE THIS IP?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-503">
            Get your license now and start creating products with this premium
            IP.
          </p>
          <button onClick={() => window.location.href = 'https://portal.spaceport.xyz/license/buy/bundle/eJwNyskRwDAIBLCGsjOA1wflcDj9l5DoLbXNfU3h4wg43kSe5aCEU1m62h7e3X1XYwyf4CQR1oVZFWGSOv7kO7skDZU3QTOBKxsaPNn6Rm35APmKHI8?distributionAuth=printify'} className="flex items-center justify-center gap-3 px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-2xl font-light rounded-full hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 mx-auto whitespace-nowrap" data-id="element-504">
            <span data-id="element-505">Get Your License</span>
            <ArrowRight className="w-6 h-6" data-id="element-506" />
          </button>
        </section>
      </div>
      {/* Gallery Image Modal */}
      {selectedGalleryImage && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedGalleryImage(null)} data-id="element-507">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()} data-id="element-508">
            <button onClick={() => setSelectedGalleryImage(null)} className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-509">
              <X className="w-6 h-6" data-id="element-510" />
            </button>
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl p-4" data-id="element-511">
              <img src={selectedGalleryImage} alt="Gallery preview" className="w-full h-auto max-h-[80vh] object-contain" data-id="element-512" />
            </div>
          </motion.div>
        </div>}
      {/* Video Modal */}
      {isVideoModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={handleCloseVideo} data-id="element-513">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()} data-id="element-514">
            <button onClick={handleCloseVideo} className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-515">
              <X className="w-6 h-6" data-id="element-516" />
            </button>
            <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl" data-id="element-517">
              <VideoPlayer src="https://video.squarespace-cdn.com/content/v1/6356d3a6954945102e51363f/b6b40d9c-68b1-44cb-99c2-3ca936c099b9/playlist.m3u8" autoPlay={true} controls={true} data-id="element-518" />
            </div>
          </motion.div>
        </div>}
      {/* Footer */}
      <Footer data-id="element-519" />
    </div>;
}